import { monthName, monthToRange } from '@/js/core/utils/month-range.js';

// The boundary month is owned by the backend (AcademicYearPolicy::START_MONTH)
// and delivered synchronously in the boot payload. The fallback only covers a
// stale plugin that doesn't provide the key yet and must match the policy.
const START_MONTH = window.gsdNowAppData?.appConfiguration?.academicYearStartMonth ?? 6;

// Semester terms are institutional and do not move with the year boundary.
const SEMESTER_MONTHS = {
  spring: [1, 2, 3, 4, 5],
  summer: [6, 7],
  fall: [8, 9, 10, 11, 12],
};

export function academicYearStartForDate(date = new Date()) {
  const month = date.getMonth() + 1;

  return month >= START_MONTH ? date.getFullYear() : date.getFullYear() - 1;
}

/**
 * Academic start year for a month key like "2024-03" or "2024-03-01".
 */
export function academicYearStartForMonthKey(key) {
  const [year, month] = key.split('-').map(Number);

  return month >= START_MONTH ? year : year - 1;
}

export function yearForAcademicMonth(startYear, month) {
  const monthNumber = Number(month);

  return monthNumber >= START_MONTH ? startYear : startYear + 1;
}

export function semesterForMonthNumber(month) {
  return Object.keys(SEMESTER_MONTHS).find(semester =>
    SEMESTER_MONTHS[semester].includes(Number(month))
  );
}

function academicYearMonths() {
  return Array.from({ length: 12 }, (_, index) =>
    String(((START_MONTH - 1 + index) % 12) + 1).padStart(2, '0')
  );
}

/**
 * The academic year's months grouped into consecutive same-semester runs, for
 * the month-selection dropdown. A semester split by the year boundary (e.g. a
 * July year-start splits summer) yields one group per run.
 */
export function academicYearMonthGroups() {
  const groups = [];

  for (const month of academicYearMonths()) {
    const semester = semesterForMonthNumber(month);
    const currentGroup = groups.at(-1);

    if (currentGroup?.semester === semester) {
      currentGroup.months.push({ id: month, label: monthName(month) });
    } else {
      groups.push({
        id: `${semester}-${month}`,
        semester,
        name: semester.charAt(0).toUpperCase() + semester.slice(1),
        months: [{ id: month, label: monthName(month) }],
      });
    }
  }

  return groups;
}

export function monthToAcademicYearRange(startYear, month) {
  return monthToRange(yearForAcademicMonth(startYear, month), month);
}

export function academicYearMonthRanges(startYear) {
  return academicYearMonths().map(month => monthToAcademicYearRange(startYear, month));
}

export function sortDateRanges(ranges) {
  return [...ranges].sort((a, b) => {
    if (a.start_after !== b.start_after) {
      return a.start_after.localeCompare(b.start_after);
    }

    return a.start_before.localeCompare(b.start_before);
  });
}
